import { Box, Spinner } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { Batch } from '@common/interfaces/api/batchesApi';
import type { DeclarationWithActivities } from '@common/interfaces/api/declarationsApi';
import type { IPractitioner } from '@common/interfaces/api/practitionerApi';
import type { CategorizedFinancialItems } from '@frontend/components/pages/processes/adminProcessFinancials/utils/categorizeItems';
import { ActivityType, type FinancialLogMutationType, ReportCategories } from '@common/enums';
import { YesNoReadOnlyFormElement } from '@frontend/components/elements/YesNoReadOnlyFormElement';
import { NoPayoutReason } from '@frontend/components/pages/processes/adminProcessFinancials/NoPayoutReason';
import { PaymentInformationForActivity } from '@frontend/components/pages/processes/adminProcessFinancials/PaymentInformationForActivity';
import { PayoutRecords } from '@frontend/components/pages/processes/adminProcessFinancials/PayoutRecords';
import { categorizeFinancialItems } from '@frontend/components/pages/processes/adminProcessFinancials/utils/categorizeItems';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import type {
    AdminProcess,
    FundingExclusion,
    FundingExclusionWithoutPractitioner,
    PayoutRecord,
} from '../adminProcessDetailsTypes';
import { FinancialStatus } from './FinancialStatus';
import { FundingSetTo } from './FundingSetTo';

type AdminProcessDetailsFinancialStatusProps = {
    financialStatus: AdminProcess;
};

export type FinancialLogMutation = {
    diagnosticReportId: string | null;
    mutationDate: string;
    mutationReason: string;
    mutationType: FinancialLogMutationType;
    practitioner: Pick<IPractitioner, 'nameFamily' | 'nameGiven' | 'nameInfix'>;
    procedureId: string | null;
    requestId: string | null;
    supervisionId: string | null;
};

const initialFinanceItem: CategorizedFinancialItems<PayoutRecord> = { completed: [], pending: [] };

export type DeclarationWithActivitiesAndBatch = DeclarationWithActivities & { exportBatch?: Batch };

export function FinancialSectionOld({
    financialStatus: {
        invoiceOrDeclarationSurpressed,
        fundingType,
        diagnosticReports,
        noPayoutReason,
        isUnpaid,
        invoiceRecords,
        payoutRecords,
        procedure,
        supervision,
        processId,
    },
}: AdminProcessDetailsFinancialStatusProps) {
    const translate = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();
    const [financialLogMutations, setFinancialLogMutations] = useState<FinancialLogMutation[]>([]);
    const [fundingExclusion, setFundingExclusion] = useState<FundingExclusion>();

    const [isLoading, setIsLoading] = useState(false);

    const [payoutItems, setPayoutItems] = useState<CategorizedFinancialItems<PayoutRecord>>(initialFinanceItem);

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            await loadFinancialStatus();
            setIsLoading(false);
        };
        load();
    }, []);

    useEffect(() => {
        setPayoutItems(categorizeFinancialItems(payoutRecords || [], (record) => !record.runNumber));
    }, [isLoading]);

    const reportCategory = diagnosticReports ? diagnosticReports[0].category : null;

    const reportActivityType =
        reportCategory === ReportCategories.examinationReport ? ActivityType.grading : ActivityType.consultation;

    const isDiagnosticReportUnpaid = Boolean(diagnosticReports?.[0]?.isUnpaid);
    const isBiometricsUnpaid = Boolean(procedure?.isUnpaid);
    const isQualityControlUnpaid = Boolean(supervision?.isUnpaid);

    const activityPayouts = [
        { activityType: ActivityType.request, shouldRender: isUnpaid },
        { activityType: ActivityType.biometrics, shouldRender: isBiometricsUnpaid },
        { activityType: reportActivityType, shouldRender: isDiagnosticReportUnpaid },
        { activityType: ActivityType.qualityControl, shouldRender: isQualityControlUnpaid },
    ];

    return (
        <>
            {isLoading ? (
                <Box>
                    <Spinner />
                </Box>
            ) : (
                <>
                    <YesNoReadOnlyFormElement
                        label={translate.request.invoiceDeclSurpressed}
                        value={Boolean(invoiceOrDeclarationSurpressed)}
                    />
                    <FundingSetTo
                        fundingExclusion={fundingExclusion}
                        fundingType={fundingType}
                    />

                    {noPayoutReason === 'waitingForConsultOrGrading' && (
                        <NoPayoutReason noPayoutReason={noPayoutReason} />
                    )}

                    {activityPayouts.map(
                        ({ shouldRender, activityType }, index) =>
                            shouldRender && (
                                <PaymentInformationForActivity
                                    activityType={activityType}
                                    financialLogMutations={financialLogMutations}
                                    key={`${index}-${activityType}`}
                                />
                            )
                    )}
                    <FinancialStatus
                        invoiceRecords={invoiceRecords}
                        processNumber={Number(processId)}
                    />
                    <PayoutRecords
                        label={translate.finance.toBePaid}
                        payoutRecords={payoutItems?.pending}
                        showRunNumber={false}
                    />

                    <PayoutRecords
                        label={translate.finance.paid}
                        payoutRecords={payoutItems?.completed}
                        showRunNumber={true}
                    />
                </>
            )}
        </>
    );

    async function loadFinancialStatus() {
        await loadLogMutations();
        await loadFundingExclusion();
    }

    async function loadFundingExclusion() {
        const fundingExclusionResult: FundingExclusionWithoutPractitioner = (
            await abortableFetch.fetchJson(`/api/finance/fundingExclusions/${processId}`, 'GET')
        ).json;

        if (fundingExclusionResult) {
            const practitioner: IPractitioner = (
                await abortableFetch.fetchJson(`/api/practitioners/${fundingExclusionResult.practitionerId}`, 'GET')
            ).json;

            setFundingExclusion({
                ...fundingExclusionResult,
                practitioner: {
                    nameFamily: practitioner.nameFamily,
                    nameGiven: practitioner.nameGiven,
                    nameInfix: practitioner.nameInfix,
                },
            });
        } else {
            setFundingExclusion(undefined);
        }
    }

    async function loadLogMutations() {
        const result = await abortableFetch.fetchJson(`/api/financialLogMutations/${processId}`, 'GET');
        setFinancialLogMutations(result.json.logMutations);
    }
}
